// aiVoice.ts — the Accent Lab's MODEL VOICE through the Oriora AI speech stack (2026-08-28).
// Bogdan: "why need a warning for which voice — isn't the AI delivering every job?" — correct.
// Device TTS was the wrong default: it varies per device, iOS's default en-GB is robotic, and
// Mandarin may simply be missing. This speaks the sentence through the SAME attested realtime
// chain the live call uses (server-minted ticket, per-language routing, per-turn billing) in a
// one-shot text-in → speech-out session with a server-locked verbatim-reader prompt.
//
// Cost discipline: the drill banks are FIXED sentences, so every spoken sentence is cached
// on-device (in-memory + best-effort localStorage) — the first play of a sentence is billed,
// every replay is free. Device TTS remains the silent fallback (offline / no funds / mint fail).

import { getAccessToken } from "./supabase";

const SERVER = "https://api.orioralabs.com";
const APP_ID = "ielts_speaking_coach";

interface SessionTicket { ws_url: string; token: string; native_model: string }

// ── cache ─────────────────────────────────────────────────────────────────────
const memCache = new Map<string, Float32Array>(); // decoded 24k mono PCM
const lsKey = (k: string) => `accent.voice.${k}`;
const hash = (s: string): string => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(36);
};

function fromLs(k: string): Float32Array | null {
  try {
    const b64 = localStorage.getItem(lsKey(k));
    if (!b64) return null;
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return pcm16ToFloat(bytes);
  } catch { return null; }
}

function toLs(k: string, pcmBytes: Uint8Array): void {
  try {
    if (pcmBytes.length > 900_000) return; // ~19s @24k — don't blow the localStorage budget
    let s = "";
    for (let i = 0; i < pcmBytes.length; i++) s += String.fromCharCode(pcmBytes[i]);
    localStorage.setItem(lsKey(k), btoa(s));
  } catch { /* quota — in-memory cache still holds it */ }
}

function pcm16ToFloat(bytes: Uint8Array): Float32Array {
  const n = Math.floor(bytes.length / 2);
  const out = new Float32Array(n);
  const dv = new DataView(bytes.buffer, bytes.byteOffset, n * 2);
  for (let i = 0; i < n; i++) out[i] = dv.getInt16(i * 2, true) / 32768;
  return out;
}

// ── playback ──────────────────────────────────────────────────────────────────
let playCtx: AudioContext | null = null;

async function playPcm(pcm: Float32Array, onEnd: () => void): Promise<void> {
  if (!playCtx) playCtx = new AudioContext();
  if (playCtx.state === "suspended") await playCtx.resume().catch(() => {});
  const buf = playCtx.createBuffer(1, pcm.length, 24000);
  buf.getChannelData(0).set(pcm);
  const src = playCtx.createBufferSource();
  src.buffer = buf;
  src.connect(playCtx.destination);
  src.onended = onEnd;
  src.start();
}

// ── the one-shot speak session ────────────────────────────────────────────────
async function fetchSpeech(text: string, language: string): Promise<Uint8Array> {
  const token = await getAccessToken();
  if (!token) throw new Error("signed out");
  const res = await fetch(`${SERVER}/v1/realtime/session`, {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
    body: JSON.stringify({ app_id: APP_ID, language, mode: "speak" }),
  });
  if (!res.ok) throw new Error(`session ${res.status}`);
  const { ticket } = (await res.json()) as { ticket: SessionTicket };

  return await new Promise<Uint8Array>((resolve, reject) => {
    const chunks: Uint8Array[] = [];
    const ws = new WebSocket(`${ticket.ws_url}?access_token=${encodeURIComponent(ticket.token)}`);
    const bail = (why: string) => { try { ws.close(); } catch { /* closed */ } reject(new Error(why)); };
    const timer = setTimeout(() => bail("speak timeout"), 20_000);
    ws.onerror = () => { clearTimeout(timer); bail("socket error"); };
    // Protocol: send the setup frame first, wait for setupComplete, THEN the text turn —
    // skipping setup hangs the socket silently (found in verification 2026-08-28).
    ws.onopen = () => {
      ws.send(JSON.stringify({ setup: { model: ticket.native_model, generationConfig: { responseModalities: ["AUDIO"] } } }));
    };
    ws.onmessage = async (ev) => {
      const raw = typeof ev.data === "string" ? ev.data : await (ev.data as Blob).text();
      let j: any; try { j = JSON.parse(raw); } catch { return; }
      if (j.setupComplete) {
        // realtimeInput.text, NOT clientContent: on this endpoint a clientContent turn is
        // answered with transcription only (0 audio tokens) — realtimeInput is the path that
        // produces spoken audio (proven live 2026-08-28: 81 AUDIO tokens, 138KB speech).
        ws.send(JSON.stringify({ realtimeInput: { text } }));
        return;
      }
      const sc = j.serverContent;
      if (!sc) return;
      for (const p of sc.modelTurn?.parts ?? []) {
        if (p.inlineData?.data) {
          const bin = atob(p.inlineData.data);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          chunks.push(bytes);
        }
      }
      if (sc.turnComplete) {
        clearTimeout(timer);
        try { ws.close(); } catch { /* done */ }
        const total = chunks.reduce((n, c) => n + c.length, 0);
        if (total === 0) { reject(new Error("no audio")); return; }
        const all = new Uint8Array(total);
        let off = 0;
        for (const c of chunks) { all.set(c, off); off += c.length; }
        resolve(all);
      }
    };
  });
}

/** Speak `text` in the AI model voice. Resolves true when AI audio PLAYED (onEnd fired after the
 *  clip), false when unavailable — caller falls back to device TTS. Cached per sentence. */
export async function speakAI(
  text: string,
  language: string,
  cb: { onStart?: () => void; onEnd?: () => void },
): Promise<boolean> {
  const key = hash(`${language}::${text}`);
  let pcm = memCache.get(key) ?? fromLs(key);
  if (!pcm) {
    let bytes: Uint8Array;
    try { bytes = await fetchSpeech(text, language); } catch { return false; }
    pcm = pcm16ToFloat(bytes);
    memCache.set(key, pcm);
    toLs(key, bytes);
  } else {
    memCache.set(key, pcm);
  }
  cb.onStart?.();
  await playPcm(pcm, () => cb.onEnd?.());
  return true;
}
